import { ReactNode } from "react";
import {
    Box,
    Card,
    CardActions,
    CardContent,
    Link,
    Stack,
    Typography
} from "@mui/material";
import { ClientAppAvatar } from "./ClientAppAvatar.tsx";
import type { IClientAppSummary } from "@ids/shared";

interface InboxListItemProps {
    app?: Pick<IClientAppSummary, "name" | "icon"> | null;
    sourceLabel: string;
    title: string;
    body?: string;
    footer?: string;
    link?: string;
    linkTitle?: string;
    date?: string;
    emphasized?: boolean;
    dimmed?: boolean;
    actions?: ReactNode;
}

export const InboxListItem = ({
    app,
    sourceLabel,
    title,
    body,
    footer,
    link,
    linkTitle,
    date,
    emphasized = false,
    dimmed = false,
    actions
}: InboxListItemProps) => {
    return (
        <Card
            variant="subtle"
            sx={{
                width: "100%",
                borderRadius: { xs: 0, sm: 2 },
                opacity: dimmed ? 0.85 : 1
            }}
        >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                    <ClientAppAvatar
                        app={app}
                        emphasized={emphasized}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "flex-start", sm: "center" }}
                            spacing={0.5}
                            sx={{ mb: 1 }}
                        >
                            <Typography
                                variant="subtitle2"
                                color={emphasized ? "primary.main" : "text.secondary"}
                                sx={{ fontWeight: 600 }}
                            >
                                {sourceLabel}
                            </Typography>
                            {date ? (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ flexShrink: 0 }}
                                >
                                    {date}
                                </Typography>
                            ) : null}
                        </Stack>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                mb: body || footer || link ? 1 : 0
                            }}
                        >
                            {title}
                        </Typography>
                        {body ? (
                            <Typography
                                variant="body1"
                                sx={{ whiteSpace: "pre-wrap" }}
                            >
                                {body}
                            </Typography>
                        ) : null}
                        {footer ? (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ whiteSpace: "pre-wrap", mt: 1 }}
                            >
                                {footer}
                            </Typography>
                        ) : null}
                        {link ? (
                            <Typography
                                variant="body2"
                                sx={{ mt: footer ? 0.5 : 1 }}
                            >
                                <Link
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {linkTitle?.trim() || "Link öffnen"}
                                </Link>
                            </Typography>
                        ) : null}
                    </Box>
                </Stack>
            </CardContent>
            {actions ? (
                <CardActions
                    sx={{
                        justifyContent: "flex-end",
                        px: { xs: 2, sm: 3 },
                        pb: { xs: 2, sm: 3 },
                        pt: 0,
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 1,
                        "& .MuiButton-root": {
                            width: { xs: "100%", sm: "auto" }
                        }
                    }}
                >
                    {actions}
                </CardActions>
            ) : null}
        </Card>
    );
};
